import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type PropsWithChildren,
} from "react";

import clsx from "clsx";
import { isNil } from "lodash";
import { twMerge } from "tailwind-merge";

interface Props extends ComponentPropsWithoutRef<"span"> {
  variant?: "default" | "notice" | "information" | "pending" | "alert";
  number?: number;
  buttonClassName?: string;
  onClear?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const Pill = forwardRef<HTMLSpanElement, PropsWithChildren<Props>>(
  (
    {
      variant = "default",
      number,
      className,
      buttonClassName,
      children,
      onClear,
      ...props
    },
    ref,
  ) => {
    return (
      <span
        ref={ref}
        {...props}
        className={twMerge(
          "inline-flex items-center justify-center gap-1 h-8 font-bold text-2xs",
          "border border-v2-gray/200 rounded-full text-v2-interstellar/300",
          "bg-v2-cosmic/50 hover:bg-v2-gray/25 px-3",
          clsx({
            "pl-1": !isNil(number),
            "pr-2": onClear,
            "text-v2-alien/600 bg-v2-alien/200 border-v2-alien/300 hover:bg-v2-alien/300":
              variant === "notice",
            "text-v2-cosmic/400 bg-v2-cosmic/100 border-v2-cosmic/400 hover:bg-v2-cosmic/200":
              variant === "information",
            "text-v2-sun/700 bg-v2-sun/200 border-v2-sun/600 hover:bg-v2-sun/300":
              variant === "pending",
            "text-v2-venus/600 bg-v2-venus/200 border-v2-venus/600 hover:bg-v2-venus/300":
              variant === "alert",
          }),
          className,
        )}
      >
        {!isNil(number) && (
          <span
            className={clsx(
              "inline-flex items-center justify-center w-6 h-6 bg-white rounded-full",
              "shadow-[0_0_2px] shadow-v2-black/10% text-xs",
            )}
          >
            {number}
          </span>
        )}
        {children}
        {onClear && (
          <button
            className={twMerge(
              clsx(
                "w-3.5 h-3.5 inline-flex items-center justify-center rounded-full",
                "hover:bg-v2-gray/50/15%",
                buttonClassName,
              ),
            )}
            onClick={onClear}
          >
            <i className="fa-regular fa-xmark text-xs"></i>
          </button>
        )}
      </span>
    );
  },
);
